// Calendario público de torneos.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  TournamentStatus,
  UserWithToken,
} from '../api.types';

// Calendar types
export type CalendarTournamentOrganizer = {
  organizerId: number;
  organizerName: string | null;
  organizerLogo: string | null;
  isPrimary: boolean;
};

/** Ventana planificada de una ronda ("Ronda 2", "Semifinal"). */
export type CalendarTournamentRound = {
  label: string;
  isTopCut: boolean;
  startDate: string;
  endDate: string;
};

export type CalendarTournament = {
  id: number;
  title: string;
  dateFrom: string | null;
  dateTo: string | null;
  status: TournamentStatus;
  tier: string | null;
  totalPlayers: number | null;
  organizerId: number | null;
  organizerName: string | null;
  organizerLogo: string | null;
  locationName: string | null;
  locationCity: string | null;
  locationCountry: string | null;
  formatId: number;
  formatName: string | null;
  organizers: CalendarTournamentOrganizer[] | null;
  roundWindows: CalendarTournamentRound[];
};

/**
 * Ultra-lightweight calendar endpoint
 * Cached by month for maximum cache hits
 *
 * `withRoundWindows` suma los torneos que arrancaron en otro mes pero juegan
 * alguna ronda en este: sólo para quien pinta las rondas por día.
 */
export const getCalendarTournaments = async (
  year: number,
  month: number,
  { withRoundWindows = false }: { withRoundWindows?: boolean } = {}
) =>
  getGraphQLQueryClient(null).request<{ data: CalendarTournament[] }>(
    gql`
      query calendarTournaments(
        $year: Int!
        $month: Int!
        $withRoundWindows: Boolean
      ) {
        data: calendarTournaments(
          year: $year
          month: $month
          withRoundWindows: $withRoundWindows
        ) {
          data {
            id
            title
            dateFrom
            dateTo
            status
            tier
            totalPlayers
            organizerId
            organizerName
            organizerLogo
            locationName
            locationCity
            locationCountry
            formatId
            formatName
            organizers {
              organizerId
              organizerName
              organizerLogo
              isPrimary
            }
            roundWindows {
              label
              isTopCut
              startDate
              endDate
            }
          }
        }
      }
    `,
    { year, month, withRoundWindows }
  );

/**
 * Get tournament IDs where the current user participated
 * Returns empty array if not authenticated
 */
export const getMyTournamentIds = async (
  user: Nullable<UserWithToken>,
  year: number,
  month: number
) =>
  getGraphQLQueryClient(user).request<{ data: number[] }>(
    gql`
      query myTournamentIds($year: Int!, $month: Int!) {
        data: myTournamentIds(year: $year, month: $month)
      }
    `,
    { year, month }
  );
